import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
    buildTextSearchUrl,
    DEFAULT_SMART_SEARCH_SETTINGS,
    detectWrongTab,
    interpretSearch,
    SEARCH_TYPE_LABELS,
    SEARCH_TYPE_PLACEHOLDERS,
    shortcutUrl,
    type SmartSearchOrigin,
    trackSearchEvent,
} from '@/lib/smart-search';
import { cn } from '@/lib/utils';
import { type SharedData } from '@/types';
import { type SearchType, type SmartSearchInterpretResponse } from '@/types/search-criteria';
import { router, usePage } from '@inertiajs/react';
import { ArrowRight, Search, Sparkles } from 'lucide-react';
import { type FormEvent, useEffect, useId, useMemo, useState } from 'react';

interface SmartSearchFormProps {
    type: SearchType;
    /** GA4 "origem" of busca_ia_enviada. */
    origin: SmartSearchOrigin;
    initialText?: string;
    placeholder?: string;
    /** Shows the clickable example phrases of the admin. */
    showExamples?: boolean;
    /** Shows the category shortcuts ("Lanchas", "Jet skis"...). */
    showShortcuts?: boolean;
    /** "Prefiro escolher nos filtros" link: opens the drawer when a handler is given, else goes to the results with it open. */
    onPreferFilters?: () => void;
    showPreferFilters?: boolean;
    /** Called when the visitor accepts the "wrong tab" hint. */
    onSwitchType?: (type: SearchType) => void;
    /** Called instead of navigating (the results page handles the navigation itself). */
    onResult?: (result: SmartSearchInterpretResponse) => void;
    /** Called when the phrase is submitted (before the request). */
    onSubmitStart?: () => void;
    /** Extra parameters appended to the final URL (e.g. the condition pills of the home). */
    extraParams?: Record<string, string>;
    autoFocus?: boolean;
    size?: 'md' | 'lg';
    className?: string;
    inputClassName?: string;
    /** Compact mode: no title, used in the sticky bar of the results. */
    compact?: boolean;
}

export const AI_BADGE_LABEL = 'IA';

/** Small "IA" badge shown next to the smart search fields. */
export function AiBadge({ className }: { className?: string }) {
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold tracking-wide text-violet-800 uppercase',
                className,
            )}
            title="Busca inteligente: escreva do seu jeito e transformamos em filtros"
        >
            <Sparkles aria-hidden="true" className="h-3 w-3" />
            {AI_BADGE_LABEL}
        </span>
    );
}

/** Appends extra parameters (e.g. condition=new) to a search URL returned by the server. */
const withExtraParams = (url: string, extra: Record<string, string>): string => {
    const entries = Object.entries(extra).filter(([, value]) => value !== '');

    if (entries.length === 0) {
        return url;
    }

    const [path, query = ''] = url.split('?');
    const params = new URLSearchParams(query);

    for (const [key, value] of entries) {
        if (!params.has(key)) {
            params.set(key, value);
        }
    }

    return `${path}?${params.toString()}`;
};

/**
 * Phrase field of the smart search (CN-200): the visitor writes the way they talk, the server turns it into filters and
 * the browser opens the results. With the AI off, slow or confused, the same phrase runs as a text search, silently.
 */
export function SmartSearchForm({
    type,
    origin,
    initialText = '',
    placeholder,
    showExamples = true,
    showShortcuts = false,
    onPreferFilters,
    showPreferFilters = false,
    onSwitchType,
    onResult,
    onSubmitStart,
    extraParams = {},
    autoFocus = false,
    size = 'lg',
    className,
    inputClassName,
    compact = false,
}: SmartSearchFormProps) {
    const { smartSearch } = usePage<SharedData>().props;
    const settings = smartSearch ?? DEFAULT_SMART_SEARCH_SETTINGS;
    const inputId = useId();
    const [text, setText] = useState(initialText);
    const [isInterpreting, setIsInterpreting] = useState(false);

    useEffect(() => {
        setText(initialText);
    }, [initialText]);

    const examples = settings.example_phrases[type] ?? [];
    const shortcuts = settings.shortcuts[type] ?? [];
    const aiEnabled = settings.enabled[type] ?? false;
    const wrongTab = useMemo(() => detectWrongTab(text, type, settings), [settings, text, type]);
    const isEmpresas = type === 'empresas';
    const accentButton = isEmpresas ? 'bg-[#0f766e] hover:bg-[#0b5f58]' : 'bg-blue-800 hover:bg-blue-900';

    const submit = async (phrase: string, fromExample = false) => {
        const trimmed = phrase.trim();

        if (!trimmed || isInterpreting) {
            return;
        }

        if (fromExample) {
            trackSearchEvent('busca_ia_exemplo_click', type);
        }

        trackSearchEvent('busca_ia_enviada', type, { origem: origin, ia_ligada: aiEnabled });
        onSubmitStart?.();

        if (!aiEnabled) {
            // Feature off for this type: today's text search, no round-trip to the AI.
            const url = withExtraParams(buildTextSearchUrl(trimmed, type), extraParams);
            const result: SmartSearchInterpretResponse = {
                type,
                text: trimmed,
                status: 'unavailable',
                understood: false,
                criteria: null,
                url,
                result_count: null,
                from_cache: false,
            };

            if (onResult) {
                onResult(result);
            } else {
                router.visit(url);
            }

            return;
        }

        setIsInterpreting(true);

        try {
            const result = await interpretSearch(trimmed, type);

            if (!result) {
                return;
            }

            if (result.status === 'not_understood') {
                trackSearchEvent('busca_ia_nao_entendida', type);
            }

            const finalResult = { ...result, url: withExtraParams(result.url, extraParams) };

            if (onResult) {
                onResult(finalResult);
            } else {
                router.visit(finalResult.url);
            }
        } finally {
            setIsInterpreting(false);
        }
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        void submit(text);
    };

    const handleWrongTabClick = () => {
        if (!wrongTab) {
            return;
        }

        trackSearchEvent('busca_aviso_aba_click', type, { para: wrongTab });
        onSwitchType?.(wrongTab);
    };

    return (
        <div className={cn('space-y-3', className)}>
            <form onSubmit={handleSubmit} role="search" aria-busy={isInterpreting} className="space-y-2">
                <label htmlFor={inputId} className="sr-only">
                    {placeholder ?? SEARCH_TYPE_PLACEHOLDERS[type]}
                </label>
                <div className="flex flex-col gap-2 sm:flex-row">
                    <div className="relative flex-1">
                        <Search aria-hidden="true" className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                            id={inputId}
                            type="search"
                            enterKeyHint="search"
                            autoComplete="off"
                            autoFocus={autoFocus}
                            value={text}
                            maxLength={settings.max_text_length}
                            onChange={(event) => setText(event.target.value)}
                            placeholder={placeholder ?? SEARCH_TYPE_PLACEHOLDERS[type]}
                            className={cn(
                                'w-full rounded-lg border border-gray-300 bg-white pr-14 pl-9 text-gray-900 placeholder:text-gray-400 focus:border-blue-800 focus:ring-2 focus:ring-blue-800/20 focus:outline-none',
                                size === 'lg' ? 'h-12 text-base' : 'h-11 text-sm',
                                isEmpresas && 'focus:border-[#0f766e] focus:ring-[#0f766e]/20',
                                inputClassName,
                            )}
                        />
                        {aiEnabled && <AiBadge className="absolute top-1/2 right-2 -translate-y-1/2" />}
                    </div>
                    <Button
                        type="submit"
                        disabled={isInterpreting || !text.trim()}
                        className={cn('shrink-0 text-white', accentButton, size === 'lg' ? 'h-12 px-6 text-base' : 'h-11 px-4', compact && 'sm:w-auto')}
                    >
                        {isInterpreting ? <Spinner aria-label="Entendendo sua busca" /> : <Search aria-hidden="true" className="h-4 w-4" />}
                        {isInterpreting ? 'Entendendo sua busca...' : 'Buscar'}
                    </Button>
                </div>

                {wrongTab && onSwitchType && (
                    <p role="status" className="text-sm text-amber-800">
                        {type === 'embarcacoes' ? 'Parece que você procura um serviço.' : 'Parece que você procura uma embarcação.'}{' '}
                        <button type="button" onClick={handleWrongTabClick} className="inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline">
                            Buscar em {SEARCH_TYPE_LABELS[wrongTab]}
                            <ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
                        </button>
                    </p>
                )}
            </form>

            {showExamples && examples.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-500">Exemplos:</span>
                    {examples.map((example) => (
                        <button
                            key={example}
                            type="button"
                            onClick={() => {
                                setText(example);
                                void submit(example, true);
                            }}
                            disabled={isInterpreting}
                            className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-left text-xs text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-100 sm:text-sm"
                        >
                            {example}
                        </button>
                    ))}
                </div>
            )}

            {showShortcuts && shortcuts.length > 0 && (
                <div className="flex flex-wrap gap-2" aria-label="Atalhos">
                    {shortcuts.map((shortcut) => (
                        <a
                            key={shortcut}
                            href={shortcutUrl(shortcut, type)}
                            className={cn(
                                'rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
                                isEmpresas ? 'bg-[#0f766e]/10 text-[#0f766e] hover:bg-[#0f766e]/20' : 'bg-blue-50 text-blue-800 hover:bg-blue-100',
                            )}
                        >
                            {shortcut}
                        </a>
                    ))}
                </div>
            )}

            {showPreferFilters && (
                <button
                    type="button"
                    onClick={() => {
                        trackSearchEvent('busca_filtros_abertos', type, { origem: origin });

                        if (onPreferFilters) {
                            onPreferFilters();
                        } else {
                            router.visit(`/search?type=${type}&filters=1`);
                        }
                    }}
                    className="text-sm font-medium text-gray-700 underline-offset-2 hover:underline"
                >
                    Prefiro escolher nos filtros
                </button>
            )}
        </div>
    );
}
